import React, { useContext } from 'react'
import Container from '@src/components/Container'
import { Box, Flex } from '@src/components/Boxes'
import styled from '@emotion/styled'
import { graphql, useStaticQuery } from 'gatsby'
import { css } from '@emotion/core'
import Img from 'gatsby-image'
import Translate from './translation/Translate'
import { ModalContext, ModalType } from '@src/components/modals/ModalController'
import { getRelativePath } from '@src/utils/routes'

const Copyright = styled.p`
  margin-top: 30px;
  margin-bottom: 11px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
  text-align: center;
  line-height: 20px;
  opacity: 0.6;
`

type StyledFooterProps = { isBlog?: boolean }
const StyledFooter = styled.footer<StyledFooterProps>`
  margin-bottom: 0;
  padding: 60px 0 30px;
  font-family: 'Proxima Nova', sans-serif;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.6);
  background-color: ${props => (props.isBlog ? '#3a697b' : '#575b7d')};
  text-align: left;
  a {
    :hover {
      text-decoration: underline;
    }
  }
`

const LinksContainer = styled(Box)`
  padding-left: 15px;
  padding-right: 15px;

  & > ul {
    margin-bottom: 20px;
    min-height: 100px;
    padding: 0;
    list-style: none;
  }

  & > ul > li {
    padding: 0;
    list-style: none;
    margin-bottom: 1px;
  }
`
LinksContainer.defaultProps = { width: [1 / 2, 1 / 2, 1 / 6] }

const LinkHeader = styled.h4`
  color: rgba(255, 255, 255, 0.9);
  font-size: 18px;
  font-weight: 400;
  line-height: 24px;
  margin-top: 11px;
  margin-bottom: 11px;
`

const Row = styled(Flex)`
  margin-left: -15px;
  margin-right: -15px;
`
Row.defaultProps = { flexWrap: 'wrap' }

const linkStyle = css`
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  background-color: transparent;
`

const LanguageAndApps = styled(Box)`
  padding-left: 15px;
  padding-right: 15px;
`
LanguageAndApps.defaultProps = { marginLeft: [0, 0, `${100 / 12}%`], width: ['100%', '100%', 3 / 12] }

const LanguageButton = styled('button')`
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 400;
  color: #fff;
  background-color: rgba(255, 255, 255, 0.2);
  text-align: left;
  border: 0;
  border-radius: 3px;
  width: 100%;
  outline: 0;

  vertical-align: middle;
  touch-action: manipulation;
  cursor: pointer;
  background-image: none;
  white-space: nowrap;
  line-height: 1.42857143;
  user-select: none;

  &:hover {
    color: #fff;
    text-decoration: underline;
    background-color: rgba(255, 255, 255, 0.1);
  }
`

const Carret = styled.span`
  width: 0;
  height: 0;
  margin-left: 2px;
  vertical-align: middle;
  border-top: 4px solid;
  border-right: 4px solid transparent;
  border-left: 4px solid transparent;
  float: right;
  margin-top: 8px;
  border-top-color: rgba(255, 255, 255, 0.6);
`

const Logo = styled(Img)`
  width: 114px;
  margin-right: 5px;
  margin-top: 10px;
`

type FooterProps = { isBlog?: boolean }

const Footer: React.FC<FooterProps> = ({ isBlog }) => {
  const data = useStaticQuery(graphql`
    query {
      appleLogo: file(name: { eq: "app-store-btn" }) {
        childImageSharp {
          fluid(maxWidth: 400, quality: 100) {
            ...GatsbyImageSharpFluid
          }
        }
      }
      googleLogo: file(name: { eq: "google-play-btn" }) {
        childImageSharp {
          fluid(maxWidth: 400, quality: 100) {
            ...GatsbyImageSharpFluid
          }
        }
      }
    }
  `)

  const { appleLogo, googleLogo } = data

  const modalContext = useContext(ModalContext)

  function openLangModal() {
    modalContext.showModal(ModalType.LanguageChooser)
  }

  return (
    <StyledFooter isBlog={isBlog}>
      <Container>
        <Row>
          <LinksContainer>
            <LinkHeader>
              <Translate path="footer.menu.1.title" />
            </LinkHeader>
            <ul>
              <li>
                <a href={isBlog ? 'https://www.classdojo.com/about/' : getRelativePath('/about/')} css={linkStyle}>
                  <Translate path="layouts.main.aboutus" />
                </a>
              </li>
              <li>
                <a href={isBlog ? 'https://www.classdojo.com/press/' : getRelativePath('/press/')} css={linkStyle}>
                  <Translate path="layouts.main.press" />
                </a>
              </li>
              <li>
                <a href={isBlog ? 'https://www.classdojo.com/jobs/' : getRelativePath('/jobs/')} css={linkStyle}>
                  <Translate path="layouts.main.careers" />
                </a>
              </li>
              <li>
                <a target="_blank" rel="noopener noreferrer" href="http://engineering.classdojo.com" css={linkStyle}>
                  <Translate path="layouts.main.engineering" />
                </a>
              </li>
            </ul>
          </LinksContainer>

          <LinksContainer>
            <LinkHeader>
              <Translate path="footer.menu.2.title" />
            </LinkHeader>
            <ul>
              <li>
                <a target="_blank" rel="noopener noreferrer" href="https://ideas.classdojo.com" css={linkStyle}>
                  <Translate path="layouts.main.bigideas" />
                </a>
              </li>
              <li>
                <a href={isBlog ? 'https://www.classdojo.com/resources/' : getRelativePath('/resources/')} css={linkStyle}>
                  <Translate path="layouts.main.support.resources" />
                </a>
              </li>
              <li>
                <a target="_blank" rel="noopener noreferrer" href="http://blog.classdojo.com" css={linkStyle}>
                  <Translate path="layouts.main.blog" />
                </a>
              </li>
              <li>
                <a href={isBlog ? 'https://www.classdojo.com/privacycenter/' : getRelativePath('/privacycenter/')} css={linkStyle}>
                  <Translate path="layouts.main.privacy_center" />
                </a>
              </li>
            </ul>
          </LinksContainer>

          <LinksContainer>
            <LinkHeader>
              <Translate path="footer.menu.3.title" />
            </LinkHeader>
            <ul>
              <li>
                <a target="_blank" rel="noopener noreferrer" href="http://classdojo.zendesk.com" css={linkStyle}>
                  <Translate path="layouts.main.helpdesk" />
                </a>
              </li>
              <li>
                <a href={isBlog ? 'https://www.classdojo.com/contact/' : getRelativePath('/contact/')} css={linkStyle}>
                  <Translate path="layouts.main.contact" />
                </a>
              </li>
              <li>
                <a href={isBlog ? 'https://www.classdojo.com/terms/' : getRelativePath('/terms/')} css={linkStyle}>
                  <Translate path="layouts.main.terms" />
                </a>
              </li>
              <li>
                <a href={isBlog ? 'https://www.classdojo.com/privacy/' : getRelativePath('/privacy/')} css={linkStyle}>
                  <Translate path="layouts.main.privacy" />
                </a>
              </li>
            </ul>
          </LinksContainer>

          <LinksContainer>
            <LinkHeader>
              <Translate path="footer.menu.4.title" />
            </LinkHeader>
            <ul>
              <li>
                <a href={isBlog ? 'https://www.classdojo.com/wall-of-love/' : getRelativePath('/wall-of-love/')} css={linkStyle}>
                  <Translate path="footer.menu.4.1" />
                </a>
              </li>
              <li>
                <a target="_blank" rel="noopener noreferrer" href="http://facebook.com/classdojo" css={linkStyle}>
                  Facebook
                </a>
              </li>
              <li>
                <a target="_blank" rel="noopener noreferrer" href="http://twitter.com/classdojo" css={linkStyle}>
                  Twitter
                </a>
              </li>
              <li>
                <a target="_blank" rel="noopener noreferrer" href="https://instagram.com/classdojo" css={linkStyle}>
                  Instagram
                </a>
              </li>
            </ul>
          </LinksContainer>
          {!isBlog && (
            <LanguageAndApps>
              <LanguageButton onClick={openLangModal}>
                <Translate locale />
                <Carret />
              </LanguageButton>
              <Flex flexDirection={['row', 'row', 'column']}>
                <a href="https://itunes.apple.com/us/app/classdojo/id552602056?mt=8" target="_blank" rel="noopener noreferrer">
                  <Logo fluid={appleLogo.childImageSharp.fluid} loading="eager" />
                </a>
                <a href="https://play.google.com/store/apps/details?id=com.classdojo.android" target="_blank" rel="noopener noreferrer">
                  <Logo fluid={googleLogo.childImageSharp.fluid} loading="eager" />
                </a>
              </Flex>
            </LanguageAndApps>
          )}
        </Row>
        <Copyright>© ClassDojo, Inc</Copyright>
      </Container>
    </StyledFooter>
  )
}

export default Footer
